/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Children, type ReactNode } from 'react';

import { Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';

import { SurfaceCard } from '../surface-card/SurfaceCard';

/**
 * The vertical event rail shared by the deal / sale / lease detail screens.
 *
 * Exported as two pieces rather than one. `DetailTimeline` is the card: title,
 * count, rows, empty state, footer. `DetailTimelineRow` is just the rail
 * geometry and typography, and exists separately because the deals Registros
 * tab renders the same rows with no card, no header and no count around them —
 * a card-only export would have forced that tab to grow a competing row style.
 *
 * The three screens had drifted on every dimension of this shape: a 14px rail
 * against `--space-3-5`, an 8px dot against 9px, a border-punch on one screen
 * and a halo on the others, and three different row paddings. Converging them
 * is most of the point.
 */

/** Attribution chip at the end of a row's meta line. */
export interface DetailTimelinePill {
  label: string;
  /** Token reference. Omitted gives the neutral grey chip. */
  accent?: string;
}

export interface DetailTimelineItem {
  id: string;
  title: ReactNode;
  detail?: ReactNode;
  /**
   * Third line: the date, or whatever else dates the row. Shares a wrapping
   * flex line with `pill`.
   */
  meta?: ReactNode;
  pill?: DetailTimelinePill;
  /** Dot colour. Omitted gives `--border-medium`, the system-event grey. */
  accent?: string;
  /** Draws the 1.5px halo ring behind the dot. */
  ringed?: boolean;
  testId?: string;
}

export interface DetailTimelineRowProps {
  item: DetailTimelineItem;
  /**
   * Omits the connector below the dot so the line ends on the last event
   * instead of trailing into the card's padding. The list owner decides it —
   * a row cannot know whether it is last.
   */
  isLast?: boolean;
  /**
   * Extra nodes between `detail` and the meta line. The escape hatch that lets
   * a feature keep a bespoke sub-line — the deals screen's italic stage-move
   * note — without that concept leaking into the shared item type.
   */
  extra?: ReactNode;
  role?: string;
}

export interface DetailTimelineProps {
  /** Eyebrow title. Already localized. */
  title: string;
  /**
   * Muted text on the right of the header, usually a count. Hidden when
   * absent, so a screen can suppress it at zero rather than printing "0".
   */
  count?: ReactNode;
  /**
   * The rows, newest first. Ignored when `children` is given.
   */
  items?: DetailTimelineItem[];
  /**
   * Pre-rendered rows, for a feature whose rows do not fit
   * `DetailTimelineItem`. A caller passing children owns `isLast` itself.
   */
  children?: ReactNode;
  /** Rendered instead of the rows when there are none. */
  empty?: ReactNode;
  /**
   * Block below the rows, separated by a dashed rule. For a near-empty nudge
   * or a standing note. What counts as near-empty is a per-screen judgement,
   * so it is a slot rather than a branch inside this component.
   */
  footer?: ReactNode;
  className?: string;
  /** Testid on the card root. */
  testId?: string;
  /**
   * Testid on the inner `role="list"`, which only exists in the non-empty
   * branch. Use this instead of `testId` for a screen whose existing tests
   * assert the timeline is *absent* when there are no events.
   */
  listTestId?: string;
}

/**
 * The rail: a dot in the row's accent, plus the connector joining it to the
 * next row.
 *
 * @param props.accent - Dot colour token reference.
 * @param props.ringed - Whether to draw the halo.
 * @param props.isLast - Suppresses the connector.
 * @returns The rendered rail.
 */
function Rail({ accent, ringed, isLast }: { accent?: string; ringed?: boolean; isLast?: boolean }) {
  const colour = accent ?? 'var(--border-medium)';
  return (
    <div className="flex flex-col items-center gap-(--space-1)" aria-hidden="true">
      <span
        className="mt-(--space-1) size-[9px] shrink-0 rounded-(--radius-full)"
        style={{
          background: colour,
          boxShadow: ringed ? `0 0 0 1.5px ${colour}` : undefined,
        }}
      />
      {!isLast && <span className="w-px flex-1 bg-(--border-subtle)" />}
    </div>
  );
}

/**
 * The attribution chip: uppercase, tinted from the row's accent, or neutral
 * grey when the row carries none.
 *
 * @param props.pill - The chip's label and accent.
 * @returns The rendered chip.
 */
function AttributionPill({ pill }: { pill: DetailTimelinePill }) {
  return (
    <Text
      component="span"
      style={
        pill.accent
          ? { color: pill.accent, background: `color-mix(in srgb, ${pill.accent} 12%, transparent)` }
          : undefined
      }
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-(--radius-full)',
        'px-(--space-2) py-(--space-0-5) uppercase',
        'text-[length:var(--text-eyebrow)] tracking-(--tracking-eyebrow)',
        !pill.accent && 'bg-(--surface-tertiary) text-(--text-tertiary-soft)',
      )}
    >
      {pill.label}
    </Text>
  );
}

/**
 * One event row: rail on the left, stacked text on the right.
 *
 * The bottom padding sits on the right column rather than the row, so the
 * connector — which is `flex-1` inside a grid cell stretched to the row's
 * height — reaches all the way to the next dot.
 *
 * @param props.item - The event.
 * @param props.isLast - Suppresses the connector.
 * @param props.extra - Nodes inserted between detail and meta.
 * @param props.role - Defaults to `listitem`, keeping a `role="list"` parent valid.
 * @returns The rendered row.
 */
export function DetailTimelineRow({
  item,
  isLast,
  extra,
  role = 'listitem',
}: DetailTimelineRowProps) {
  return (
    <div
      role={role}
      data-testid={item.testId}
      className="grid grid-cols-[var(--space-3-5)_minmax(0,1fr)] gap-(--space-3)"
    >
      <Rail accent={item.accent} ringed={item.ringed} isLast={isLast} />

      <div className="flex min-w-0 flex-col gap-(--space-1) pb-(--space-3-5)">
        <Text
          component="span"
          className={cn(
            'text-[length:var(--text-body-sm)] [font-weight:var(--font-medium)]',
            'leading-snug text-(--text-primary)',
          )}
        >
          {item.title}
        </Text>

        {item.detail && (
          <Text component="span" className="text-[length:var(--text-meta)] text-(--text-secondary)">
            {item.detail}
          </Text>
        )}

        {extra}

        {(item.meta || item.pill) && (
          <div className="flex flex-wrap items-center gap-(--space-2)">
            {item.meta && (
              <Text
                component="span"
                className="text-[length:var(--text-meta)] tabular-nums text-(--text-tertiary-soft)"
              >
                {item.meta}
              </Text>
            )}
            {item.pill && <AttributionPill pill={item.pill} />}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * A vertical event rail in one card: eyebrow title, muted count, rows, and an
 * optional footer separated by a dashed rule.
 *
 * @param props.title - Eyebrow title.
 * @param props.count - Muted right-hand count.
 * @param props.items - The events, newest first.
 * @param props.children - Pre-rendered rows, taking precedence over `items`.
 * @param props.empty - Rendered when there are no rows.
 * @param props.footer - Block below the rows, behind a dashed rule.
 * @param props.className - Extra classes for the card.
 * @param props.testId - Testid on the card root.
 * @param props.listTestId - Testid on the inner list, absent when empty.
 * @returns The rendered timeline card.
 */
export function DetailTimeline({
  title,
  count,
  items,
  children,
  empty,
  footer,
  className,
  testId,
  listTestId,
}: DetailTimelineProps) {
  // Counted rather than null-checked: a caller mapping over its own list
  // passes `[]` when that list is empty, and an empty array is not null. The
  // difference decides whether the empty slot renders at all.
  const childCount = Children.count(children);
  const hasRows = childCount > 0 || (items?.length ?? 0) > 0;

  return (
    <SurfaceCard
      testId={testId}
      className={cn('min-w-0 gap-(--space-3) px-(--space-5) py-(--space-4)', className)}
    >
      {/* Titled like every other panel on these screens — sentence case,
          seed, ruled off — rather than as a micro-label, which is the
          caption of a figure, not the name of a card. */}
      <div className="flex items-baseline justify-between gap-(--space-2-5) border-b border-(--border-subtle) pb-(--space-3)">
        <Text
          component="span"
          className="text-[length:var(--text-app-section)] [font-weight:var(--font-bold)] text-(--seed-base)"
        >
          {title}
        </Text>
        {count != null && (
          <Text
            component="span"
            className="text-[length:var(--text-meta)] text-(--text-tertiary-soft)"
          >
            {count}
          </Text>
        )}
      </div>

      {hasRows ? (
        <div role="list" data-testid={listTestId} className="flex flex-col">
          {childCount > 0
            ? children
            : items?.map((item, index) => (
                <DetailTimelineRow key={item.id} item={item} isLast={index === items.length - 1} />
              ))}
        </div>
      ) : (
        empty
      )}

      {footer && (
        <div
          className={cn(
            'flex flex-col items-start gap-(--space-2)',
            'border-t border-dashed border-(--border-subtle) pt-(--space-3-5)',
          )}
        >
          {footer}
        </div>
      )}
    </SurfaceCard>
  );
}
